import type { ActiveCall, JoinCallResponse } from '@doneverse/shared'
import { api, getDeviceId } from '../api/index.js'

export const callService = {
  /**
   * Start a huddle in a conversation, or join the one already running.
   *
   * One endpoint for both, deliberately — Slack huddles have no separate
   * "start", and collapsing them means two people clicking at once can't create
   * two calls. `is_new` in the response says which happened.
   *
   * `device_id` claims the user's seat for this tab. Joining from a second
   * device takes the seat over and the first is ejected.
   */
  join: async (contactId: string, callId?: string) => {
    return api
      .post(
        {
          contact_id: contactId,
          device_id: getDeviceId(),
          ...(callId ? { call_id: callId } : {}),
        },
        '/v1/calls/join',
      )
      .json<JoinCallResponse>()
  },

  /**
   * The live huddle in a conversation, if any.
   *
   * Realtime events are best-effort, so this is what rebuilds the joinable pill
   * after a reload, a socket reconnect, or an event that never arrived.
   */
  getActive: async (contactId: string) => {
    const params = new URLSearchParams({ contact_id: contactId })
    return api.get(`/v1/calls/active?${params}`).json<{ call: ActiveCall | null }>()
  },

  /**
   * Hang up from THIS device.
   *
   * `stale: true` means the leave was ignored because this device had already
   * been handed off — so it couldn't end the call the user's other device is
   * still on. Nothing acts on it; it's there to make that visible in a log.
   */
  leave: async (callId: string) => {
    return api
      .post({ device_id: getDeviceId() }, `/v1/calls/${callId}/leave`)
      .json<{ ended: boolean; stale: boolean }>()
  },

  /**
   * Turn the call down. Unlike the local dismissal this replaces, it reaches
   * the caller, stops the ring on every one of this user's devices, and
   * survives a reload. Declining a DM ends the call.
   */
  decline: async (callId: string) => {
    return api.post({ device_id: getDeviceId() }, `/v1/calls/${callId}/decline`).json<{ ended: boolean }>()
  },
}
